<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <span>
                <q-icon name="fas fa-hourglass-half" class="q-mb-sm" /> {{ $t('general_tests') }}

                <q-tooltip anchor="bottom right" self="top left">
                    {{ $t('general_tests_helper') }}
                </q-tooltip>
            </span>
        </div>

        <q-table
            :data="tests"
            :columns="columns"
            :pagination.sync="server_pagination"
            @request="request"
            :loading="loading"
            :filter="filter"
            row-key="id"
            :no-data-label="$t('no_data_avaliable')"
            :no-results-label="$t('no_results_found')"
            :loading-label="$t('loading_data')"
            :rows-per-page-label="$t('items_per_page')"
            :rows-per-page-options="[5,10,50,100]"
            binary-state-sort
            >

            <template slot="top" slot-scope="props">
                <q-search v-model="filter" icon="fas fa-search" :placeholder="$t('search')" :debounce="500" />
                <div class="col" />
                <q-btn color="secondary" icon="fas fa-plus" :label="$t('new')" @click="$router.push({ name: 'admin_general_test', params: { id: 0 } })" />
            </template>

            <q-tr slot="header" slot-scope="props" :props="props">
                <q-th v-for="col in props.cols" :key="col.name" :props="props">
                {{ $t(col.label) }}
                </q-th>
            </q-tr>

            <q-tr slot="body" slot-scope="props" :props="props">
                <q-td auto-width key="id" :props="props">
                    {{ props.row.id }}
                </q-td>

                <q-td key="title" :props="props">
                    {{ props.row.title }}
                </q-td>

                <q-td key="permission" :props="props">
                    <q-btn color="primary" text-color="white" :label="getQuantity(props.row.permission)" no-ripple>
                        <q-tooltip>
                            <p v-if="props.row.permission.areas.length > 0" class="tooltip q-mb-sm">
                                {{ $t('areas') }}:
                                <span v-for="(area, index) in props.row.permission.areas" :key="index">
                                    {{ getArea(area) }}
                                    <span v-if="(props.row.permission.areas.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.clients.length > 0" class="tooltip q-mb-sm">
                                {{ $t('clients') }}:
                                <span v-for="(client, index) in props.row.permission.clients" :key="index">
                                    {{ getClient(client) }}
                                    <span v-if="(props.row.permission.clients.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.user_groups.length > 0" class="tooltip q-mb-sm">
                                {{ $t('user_groups') }}:
                                <span v-for="(user_group, index) in props.row.permission.user_groups" :key="index">
                                    {{ getUserGroup(user_group) }}
                                    <span v-if="(props.row.permission.user_groups.length - 1) > index">, </span>
                                </span>
                            </p>
                            <p v-if="props.row.permission.profiles.length > 0" class="tooltip q-mb-sm">
                                {{ $t('profiles') }}:
                                <span v-for="(profile, index) in props.row.permission.profiles" :key="index">
                                    {{ getUserProfile(profile) }}
                                    <span v-if="(props.row.permission.profiles.length - 1) > index">, </span>
                                </span>
                            </p>
                        </q-tooltip>
                    </q-btn>
                </q-td>

                <q-td key="tags" :props="props">
                    <q-btn color="primary" text-color="white" :label="getQuantityTags(props.row.tags)" no-ripple>
                        <q-tooltip v-if="props.row.tags.length > 0">
                            <p class="tooltip q-mb-sm">
                                {{ $t('tags') }}:
                                <span v-for="(tag, index) in props.row.tags" :key="index">
                                    {{ getTag(tag) }}
                                    <span v-if="(props.row.tags.length - 1) > index">, </span>
                                </span>
                            </p>
                        </q-tooltip>
                    </q-btn>
                </q-td>

                <q-td key="qty_questions" :props="props">
                    {{ props.row.qty_questions }}
                </q-td>

                <q-td key="minimum_score" :props="props">
                    {{ props.row.minimum_score }}
                </q-td>

                <q-td key="time_to_finish" :props="props">
                    {{ props.row.time_to_finish }}
                </q-td>

                <q-td key="qty_user_results" :props="props">
                    {{ props.row.qty_user_results }}
                </q-td>

                <q-td key="updated_date" :props="props">
                    {{ props.row.updated_date }}
                </q-td>

                <q-td key="status" :props="props" auto-width>
                    <q-chip small :icon="getIconStatus(props.row.status)" :color="getColorStatus(props.row.status)" class="full-width">
                        {{ $t(getStatus(props.row.status)) }}
                    </q-chip>
                </q-td>

                <q-td auto-width key="actions" :props="props">
                    <q-btn flat round color="primary" icon="fas fa-pencil-alt" @click="$router.push({ name: 'admin_general_test', params: { id: props.row.actions } })" />
                    <q-btn flat round color="secondary" icon="fas fa-chart-line" />
                    <q-btn flat round color="negative" icon="far fa-trash-alt" @click="confirmRemove(props.row.actions)" />
                </q-td>
            </q-tr>
        </q-table>

        <q-dialog v-model="confirm_remove" @ok="removeItem">
            <span slot="title">{{ $t('remove_item') }}</span>

            <span slot="message">{{ $t('confirm_remove_item') }}</span>

            <template slot="buttons" slot-scope="props">
                <q-btn color="primary" :label="$t('cancel')" @click="confirm_remove = false" />
                <q-btn color="negative" :label="$t('remove')" @click="removeItem()" />
            </template>
        </q-dialog>
    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { date } from 'quasar'

export default {
    i18nOptions: { namespaces: [ "custom", "common", "general_tests", "admin"] },

    data() {
        return {
            server_pagination: {
                page: 1,
                rowsNumber: 10,
                rowsPerPage: 5,
                descending: false,
                sortBy: null
            },
            loading: false,
            tests: [],
            columns: [
                {
                    name: 'id',
                    required: true,
                    label: '#',
                    align: 'left',
                    field: 'id',
                    sortable: true,
                },
                {
                    name: 'title',
                    required: true,
                    label: 'training',
                    align: 'left',
                    field: 'title',
                    sortable: true,
                },
                {
                    name: 'permission',
                    required: true,
                    label: 'permission',
                    align: 'left',
                    field: 'permission',
                    sortable: false,
                },
                {
                    name: 'tags',
                    required: true,
                    label: 'tags',
                    align: 'left',
                    field: 'tags',
                    sortable: false,
                },
                {
                    name: 'qty_questions',
                    required: true,
                    label: 'quantity_questions',
                    align: 'left',
                    field: 'qty_questions',
                    sortable: true,
                },
                {
                    name: 'minimum_score',
                    required: true,
                    label: 'minimum_score',
                    align: 'left',
                    field: 'minimum_score',
                    sortable: true,
                },
                {
                    name: 'time_to_finish',
                    required: true,
                    label: 'time_to_finish',
                    align: 'left',
                    field: 'time_to_finish',
                    sortable: false,
                },
                {
                    name: 'qty_user_results',
                    required: true,
                    label: 'quantity_realizations',
                    align: 'left',
                    field: 'qty_user_results',
                    sortable: true,
                },
                {
                    name: 'updated_date',
                    required: true,
                    label: 'update_date',
                    align: 'left',
                    field: 'updated_date',
                    sortable: true,
                },
                {
                    name: 'status',
                    required: true,
                    label: 'status',
                    align: 'left',
                    field: 'status',
                    sortable: true,
                },
                {
                    name: 'actions',
                    required: true,
                    label: '',
                    align: 'right',
                    field: 'edit',
                    sortable: false,
                },
            ],
            filter: '',
            action_id: '',
            confirm_remove: false,
            areas: [],
            user_groups: [],
            profiles: [],
            tags: []
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            id_user_client_country_selected: 'common/id_user_client_country_selected',
            user_clients: 'common/user_clients'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        }
    },

    methods: {
        request ({ pagination }) {
            this.loading = true
            this.server_pagination = pagination

            let order = pagination.descending ? '-' : ''
            let order_by = pagination.sortBy ? order + pagination.sortBy : ''

            let data = {
                "page_size": this.server_pagination.rowsPerPage,
                "page": this.server_pagination.page,
                "search": this.filter,
                "ordering": order_by
            }

            this.$axios.get(`/admin_general_tests/general_tests/?${this.$qs.stringify(data)}`, this.getHeader)
            .then(response => {
                this.server_pagination.rowsNumber = response.data.count

                this.tests = []

                response.data.results.forEach(result => {
                    let permission = {}

                    permission.areas = result.areas
                    permission.clients = result.clients
                    permission.user_groups = result.user_groups
                    permission.profiles = result.profiles

                    this.tests.push({
                        id: result.id,
                        title: result.title,
                        permission: permission,
                        tags: result.tags,
                        minimum_score: result.minimum_score,
                        time_to_finish: result.time_to_finish,
                        qty_questions: result.qty_questions,
                        qty_user_results: result.qty_user_results,
                        updated_date: date.formatDate(result.updated_date, 'DD/MM/YYYY'),
                        status: result.status,
                        actions: result.id
                    })
                })

                this.loading = false
            })
            .catch(e => {
                this.loading = false
                console.log(e)
            })
        },

        fetchLists() {
            this.$q.loading.show()

            this.$axios.all([this.fetchAreas(), this.fetchUserGroup(), this.fetchProfiles(), this.fetchTags()])
            .then(this.$axios.spread((areas, user_groups, profiles, tags) => {
                this.areas = []
                this.user_groups = []
                this.profiles = []
                this.tags = []

                areas.data.country_areas.forEach(country_area => {
                    this.areas.push({
                        label: country_area.name,
                        value: country_area.id
                    })
                })

                user_groups.data.forEach(user_group => {
                    this.user_groups.push({
                        label: user_group.title,
                        value: user_group.id
                    })
                })

                profiles.data.forEach(profile => {
                    this.profiles.push({
                        label: profile.name,
                        value: profile.id
                    })
                })

                tags.data.forEach(tag => {
                    this.tags.push({
                        label: tag.title,
                        value: tag.id
                    })
                })

                this.$q.loading.hide()
            }))
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        fetchAreas() {
            return this.$axios.get('/accounts/get_user_areas/', this.getHeader)
        },

        fetchUserGroup() {
            return this.$axios.get('/accounts/get_user_group_users/', this.getHeader)
        },

        fetchProfiles() {
            return this.$axios.get('/accounts/get_user_profiles/', this.getHeader)
        },

        fetchTags() {
            return this.$axios.get('/admin_tags/tags/get_tags/', this.getHeader)
        },

        confirmRemove(id) {
            this.action_id = id
            this.confirm_remove = true
        },

        removeItem() {
            this.confirm_remove = false

            this.$q.notify({
                message: this.$t('general_test_removed_success'),
                type: 'positive',
                icon: 'far fa-trash-alt'
            })

            this.request({
                pagination: this.server_pagination
            })
        },

        getStatus(status) {
            if (status == 1) return 'published'
            else if (status == 2) return 'unpublished'
            else if (status == 3) return 'deleted_f'
        },

        getIconStatus(status) {
            if (status == 1) return 'fas fa-check'
            else if (status == 2) return 'fas fa-ban'
            else if (status == 3) return 'fas fa-trash-alt'
        },

        getColorStatus(status) {
            if (status == 1) return 'positive'
            else if (status == 2) return 'warning'
            else if (status == 3) return 'negative'
        },

        getQuantity(permission) {
            return permission.areas.length + permission.clients.length + permission.user_groups.length + permission.profiles.length
        },

        getArea(area) {
            let obj = this.areas.find((obj) => { return obj.value === area })
            if (obj) return obj.label
            else return ''
        },

        getClient(client) {
            let obj = this.user_clients.find((obj) => { return obj.id === client })
            if (obj) return obj.name
            else return ''
        },

        getUserGroup(user_group) {
            let obj = this.user_groups.find((obj) => { return obj.value === user_group })
            if (obj) return obj.label
            else return ''
        },

        getUserProfile(profile) {
            let obj = this.profiles.find((obj) => { return obj.value === profile })
            if (obj) return obj.label
            else return ''
        },

        getQuantityTags(tags) {
            return tags.length > 0 ? tags.length : '0'
        },

        getTag(tag) {
            let obj = this.tags.find((obj) => { return obj.value === tag })
            if (obj) return obj.label
            else return ''
        },
    },

    created() {
        this.request({
            pagination: this.server_pagination
        })

        this.fetchLists()
    }
}
</script>

<style lang="stylus" scoped>

</style>
